import {
  DialogComponent,
  DialogOpenEvent,
  DialogCloseEvent
} from '@theme/dialog';

import { CartAddEvent } from '@theme/events';

import { isMobileBreakpoint }
  from '@theme/utilities';

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} [liveRegion]
 */

class CartDrawerComponent
  extends DialogComponent {

  #summaryThreshold = 0.5;

  #historyAbortController = null;

  connectedCallback() {

    super.connectedCallback();

    document.addEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    this.addEventListener(
      DialogOpenEvent.eventName,
      this.#updateStickyState
    );

    this.addEventListener(
      DialogOpenEvent.eventName,
      this.#handleHistoryOpen
    );

    this.addEventListener(
      DialogCloseEvent.eventName,
      this.#handleHistoryClose
    );

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {

    super.disconnectedCallback();

    document.removeEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#updateStickyState
    );

    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#handleHistoryOpen
    );

    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleHistoryClose
    );

    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {

    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {

      history.pushState(
        { cartDrawerOpen: true },
        ''
      );
    }

    this.#historyAbortController =
      new AbortController();

    window.addEventListener(
      'popstate',
      this.#handlePopState,
      {
        signal:
          this.#historyAbortController.signal
      }
    );
  };

  #handleHistoryClose = () => {

    this.#historyAbortController?.abort();

    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {

    if (this.refs.dialog?.open) {

      this.refs.dialog.style.setProperty(
        '--dialog-drawer-closing-animation',
        'none'
      );

      await this.closeDialog();

      this.refs.dialog.style.removeProperty(
        '--dialog-drawer-closing-animation'
      );
    }
  };

  #handleCartAdd = (event) => {

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(
      event.detail.resource?.item_count
    );

    this.#waitForCartDrawer();
  };

  #announceCartCount(cartCount) {

    const liveRegion =
      this.refs.liveRegion;

    if (
      !this.refs.dialog?.open ||
      !liveRegion ||
      cartCount === undefined
    ) {
      return;
    }

    liveRegion.textContent =
      `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {

    this.showDialog();

    this.#waitForCartDrawer();

    customElements.whenDefined(
      'shopify-payment-terms'
    ).then(() => {

      const installmentsContent =
        document.querySelector(
          'shopify-payment-terms'
        )?.shadowRoot;

      const cta =
        installmentsContent?.querySelector(
          '#shopify-installments-cta'
        );

      cta?.addEventListener(
        'click',
        this.closeDialog,
        { once: true }
      );

    });
  }

  close() {
    this.closeDialog();
  }

  #waitForCartDrawer = () => {

  const dialog = this.refs.dialog;

  if (!dialog) return;

  const content = dialog.querySelector(

    '.cart-drawer__content'

  );

  if (!content) return;

  let rendered = false;

  const observer = new MutationObserver(() => {

    const summary =

      dialog.querySelector(

        '.cart-drawer__summary'

      );

    const existingRelated =

      dialog.querySelector(

        '.cart-drawer-related-products'

      );

    if (

      summary &&

      !existingRelated &&

      !rendered

    ) {

      rendered = true;

      observer.disconnect();

      setTimeout(() => {

        this.#renderRelatedProducts();

      }, 200);

    }

  });

  observer.observe(content, {

    childList: true,

    subtree: true

  });

  /* fallback */

  setTimeout(() => {

    const summary =

      dialog.querySelector(

        '.cart-drawer__summary'

      );

    const existingRelated =

      dialog.querySelector(

        '.cart-drawer-related-products'

      );

    if (

      summary &&

      !existingRelated &&

      !rendered

    ) {

      rendered = true;

      observer.disconnect();

      this.#renderRelatedProducts();

    }

  }, 1000);

};

  #renderRelatedProducts = async () => {

    const { dialog } =
      this.refs;

    if (!dialog) return;

    const summaryElement =
      dialog.querySelector(
        '.cart-drawer__summary'
      );

    if (!summaryElement) return;

    /* REMOVE OLD RELATED */

    const oldRelated =
      dialog.querySelector(
        '.cart-drawer-related-products'
      );

    if (oldRelated) {
      oldRelated.remove();
    }

    /* CREATE RELATED WRAPPER */

    const relatedWrapper =
      document.createElement('div');

    relatedWrapper.className =
      'cart-drawer-related-products';

    relatedWrapper.innerHTML = `
      <div class="cart-related-heading">
        You may also like
      </div>

      <div class="cart-related-grid loading">

        <div class="cart-related-skeleton"></div>
        <div class="cart-related-skeleton"></div>
        <div class="cart-related-skeleton"></div>
        <div class="cart-related-skeleton"></div>

      </div>
    `;

    summaryElement.parentNode.insertBefore(
      relatedWrapper,
      summaryElement
    );

    try {

      const [productsData, cart] =
        await Promise.all([
          fetch('/products.json?limit=8')
            .then(res => res.json()),

          fetch('/cart.js')
            .then(res => res.json())
        ]);

      const grid =
        relatedWrapper.querySelector(
          '.cart-related-grid'
        );

      if (!grid) return;

      grid.classList.remove('loading');

      grid.innerHTML = '';

      const cartProductIds =
        cart.items.map(
          item => item.product_id
        );

      const filteredProducts =
        productsData.products.filter(
          product =>
            !cartProductIds.includes(
              product.id
            )
        );

      if (filteredProducts.length === 0) {

        relatedWrapper.innerHTML = '';

        return;
      }

      filteredProducts
        .slice(0, 4)
        .forEach(product => {

          const image =
            product.images?.[0]?.src || '';

          const variantId =
            product.variants?.[0]?.id || '';

          const price =
            product.variants?.[0]?.price || '0.00';

          grid.innerHTML += `
            <div class="cart-related-item">

              <a
                href="/products/${product.handle}"
                class="cart-related-image"
              >
                <img
                  src="${image}"
                  alt="${product.title}"
                >
              </a>

              <div class="cart-related-content">

                <a
                  href="/products/${product.handle}"
                  class="cart-related-title"
                >
                  ${product.title}
                </a>

                <div class="cart-related-price">
                  Rs. ${price}
                </div>

                <button
                  class="cart-related-add-btn"
                  data-variant-id="${variantId}"
                >
                  Add to Cart
                </button>

              </div>

            </div>
          `;
        });

    } catch (error) {

      console.error(
        'Related products error:',
        error
      );
    }

    /* ADD TO CART */

    relatedWrapper.addEventListener(
      'click',
      async (event) => {

        const button =
          event.target.closest(
            '.cart-related-add-btn'
          );

        if (!button) return;

        event.preventDefault();

        const variantId =
          button.dataset.variantId;

        if (!variantId) return;

        button.disabled = true;

        button.innerText = 'Adding...';

        try {

          await fetch('/cart/add.js', {

            method: 'POST',

            headers: {
              'Content-Type':
                'application/json'
            },

            body: JSON.stringify({
              id: variantId,
              quantity: 1
            })

          });

          // Get latest cart
          const cart = await fetch('/cart.js').then(r => r.json());

          // Dispatch CartAddEvent
          document.dispatchEvent(
            new CartAddEvent({
              resource: cart
            })
          );

        } catch (error) {

          console.error(
            'Add to cart error:',
            error
          );

          button.disabled = false;

          button.innerText =
            'Add to Cart';
        }

      }
    );
  };

  #updateStickyState = () => {

    const { dialog } =
      this.refs;

    if (!dialog) return;

    const content =
      dialog.querySelector(
        '.cart-drawer__content'
      );

    const summary =
      dialog.querySelector(
        '.cart-drawer__summary'
      );

    if (!content || !summary) {

      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect()
        .height;

    const summaryHeight =
      summary.getBoundingClientRect()
        .height;

    const ratio =
      summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  };
}

if (
  !customElements.get(
    'cart-drawer-component'
  )
) {

  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}